// ============================================================================
// BESPOKIAN Pharmacist Cockpit - Sidebar Navigation
// Main navigation sidebar with queue counts
// ============================================================================

import { Link, useLocation } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { clsx } from 'clsx';
import {
  LayoutDashboard,
  ClipboardCheck,
  AlertTriangle,
  Package,
  CheckCircle,
  Truck,
  FileText,
  Settings,
  Activity,
} from 'lucide-react';
import { queueApi } from '@/api/queue.api';
import { useAuth } from '@/contexts/AuthContext';

interface NavItem {
  to: string;
  icon: typeof LayoutDashboard;
  label: string;
  countKey?: string;
}

const NAV_ITEMS: NavItem[] = [
  { to: '/', icon: LayoutDashboard, label: 'Dashboard' },
  {
    to: '/queue',
    icon: ClipboardCheck,
    label: 'Verify',
    countKey: 'PENDING_VERIFICATION',
  },
  {
    to: '/queue/dur',
    icon: AlertTriangle,
    label: 'DUR Review',
    countKey: 'PENDING_DUR',
  },
  {
    to: '/queue/fill',
    icon: Package,
    label: 'Fill',
    countKey: 'PENDING_FILL',
  },
  {
    to: '/queue/check',
    icon: CheckCircle,
    label: 'RPh Check',
    countKey: 'PENDING_PHARMACIST_CHECK',
  },
  {
    to: '/queue/ready',
    icon: Truck,
    label: 'Ready',
    countKey: 'READY_FOR_PICKUP',
  },
];

const SECONDARY_NAV: NavItem[] = [
  { to: '/audit', icon: FileText, label: 'Audit Log' },
  { to: '/batch', icon: Activity, label: 'Batch Process' },
  { to: '/settings', icon: Settings, label: 'Settings' },
];

export function Sidebar() {
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const { data: counts } = useQuery({
    queryKey: ['queue', 'counts'],
    queryFn: () => queueApi.getQueueCounts(),
    enabled: isAuthenticated,
    refetchInterval: isAuthenticated ? 10000 : false,
  });

  return (
    <aside className="w-64 bg-slate-900 text-white flex flex-col">
      {/* Logo */}
      <div className="px-6 py-5 border-b border-slate-800">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-white flex items-center justify-center shadow-sm">
            <img
              src="/logo.svg"
              alt="BESPOKIAN Health"
              className="h-7 w-7 object-contain"
            />
          </div>
          <div>
            <div className="font-semibold text-white">BESPOKIAN</div>
            <div className="text-xs text-slate-400">Pharmacist Cockpit</div>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        <div className="text-xs font-semibold text-slate-400 uppercase px-3 mb-2">
          Workflow
        </div>
        {NAV_ITEMS.map((item) => {
          const isActive = location.pathname === item.to;
          const count = item.countKey ? counts?.[item.countKey as keyof typeof counts] : undefined;

          return (
            <Link
              key={item.to}
              to={item.to}
              className={clsx(
                'flex items-center gap-3 px-3 py-2 rounded-lg transition-colors',
                isActive
                  ? 'bg-pharmacy-primary text-white'
                  : 'text-slate-300 hover:bg-slate-800 hover:text-white'
              )}
            >
              <item.icon className="h-5 w-5 flex-shrink-0" />
              <span className="flex-1 truncate">{item.label}</span>
              {count !== undefined && count > 0 && (
                <span
                  className={clsx(
                    'text-xs font-semibold px-2 py-0.5 rounded-full',
                    isActive ? 'bg-white/20' : 'bg-slate-700'
                  )}
                >
                  {count}
                </span>
              )}
            </Link>
          );
        })}

        <div className="text-xs font-semibold text-slate-400 uppercase px-3 mt-6 mb-2">
          Tools
        </div>
        {SECONDARY_NAV.map((item) => {
          const isActive = location.pathname === item.to;

          return (
            <Link
              key={item.to}
              to={item.to}
              className={clsx(
                'flex items-center gap-3 px-3 py-2 rounded-lg transition-colors',
                isActive
                  ? 'bg-pharmacy-primary text-white'
                  : 'text-slate-300 hover:bg-slate-800 hover:text-white'
              )}
            >
              <item.icon className="h-5 w-5 flex-shrink-0" />
              <span className="flex-1 truncate">{item.label}</span>
            </Link>
          );
        })}
      </nav>

      {/* Footer */}
      <div className="px-4 py-4 border-t border-slate-800">
        <div className="text-xs text-slate-500 text-center">
          © 2025 BESPOKIAN Health
        </div>
      </div>
    </aside>
  );
}
